import type { NpcDifficulty, RacerSnapshot, StyleAction } from '../shared/types.js';
import { raceWagerDefinition } from '../shared/gameplay-fun.js';
import {
  readLocalCareerSection,
  updateLocalCareerSection,
} from './local-career-storage.js';
import { t } from './i18n/index.js';

export type ChallengeId = 'finish' | 'podium' | 'style' | 'clean' | 'risk' | 'combo';
export type ContractCadence = 'daily' | 'weekly';
export type ContractMetric = 'finish' | 'podium' | 'win' | 'style' | 'risk' | 'clean' | 'pro-finish';
export type MasteryTier = 'none' | 'bronze' | 'silver' | 'gold';
export type CareerMedal = 'none' | 'bronze' | 'silver' | 'gold';
export type CareerRewardId = 'chevron' | 'acid-paint' | 'circuit' | 'gold-paint' | 'legend-paint' | 'aurora-paint' | 'city-icon' | 'redline-paint' | 'tour-legend';

export interface ChallengeDefinition {
  id: ChallengeId;
  name: string;
  description: string;
  reward: number;
}

export interface ActiveContract {
  claimId: string;
  id: string;
  cadence: ContractCadence;
  period: string;
  name: string;
  description: string;
  reward: number;
  target: number;
  progress: number;
  claimed: boolean;
}

export interface TrackMasteryRecord {
  races: number;
  finishes: number;
  wins: number;
  podiums: number;
  bestPlace: number;
  bestStyle: number;
  bestIntegrity: number;
  bestLapMs: number | null;
  bestMedal: CareerMedal;
  bestRoutes: number;
  bestPerfectCorners: number;
  updatedAt: number;
}

export interface RivalRecord {
  name: string;
  races: number;
  winsAgainst: number;
  lossesAgainst: number;
  bestFinish: number;
  streak: number;
  carIds: string[];
  lastSeenAt: number;
}

export interface ProgressionState {
  version: 3;
  xp: number;
  completed: ChallengeId[];
  awardedRaceIds: string[];
  contractProgress: Record<string, number>;
  contractClaims: string[];
  trackMastery: Record<string, TrackMasteryRecord>;
  rivals: Record<string, RivalRecord>;
  recentResults: RecentRaceResult[];
  races: number;
  wins: number;
  bestStyle: number;
  bestCombo: number;
  updatedAt: number;
}

export interface RaceRunStats {
  maxCombo: number;
  maxMultiplier: number;
  riskLines: number;
  routes: number;
  perfectCorners: number;
  actions: Partial<Record<StyleAction, number>>;
  lastStyleEventId: number;
}

export interface RecentRaceResult {
  place: number;
  total: number;
  finished: boolean;
  difficulty: NpcDifficulty;
  at: number;
}

export interface CareerRaceContext {
  mapId: string;
  npcDifficulty: NpcDifficulty;
  timeTrial: boolean;
  racers: readonly RacerSnapshot[];
  bestLapMs: number | null;
  medal: CareerMedal;
  now: number;
  featuredRival?: string;
}

export interface XpBreakdownEntry {
  id: string;
  label: string;
  xp: number;
}

export interface TrackMasteryUpdate {
  mapId: string;
  before: ReturnType<typeof trackMasteryProgress>;
  after: ReturnType<typeof trackMasteryProgress>;
  newGoals: string[];
  xpEarned: number;
  record: TrackMasteryRecord;
}

export interface RivalDelta {
  key: string;
  name: string;
  result: 'won' | 'lost';
  record: RivalRecord;
}

export interface ProgressionUpdate {
  state: ProgressionState;
  completed: ChallengeDefinition[];
  completedContracts: ActiveContract[];
  xpBreakdown: XpBreakdownEntry[];
  xpEarned: number;
  repeatableXp: number;
  previousLevel: number;
  level: number;
  mastery: TrackMasteryUpdate | null;
  rivals: RivalDelta[];
  duplicate: boolean;
}

export interface CareerRewardDefinition {
  id: CareerRewardId;
  level: number;
  name: string;
  kind: 'decal' | 'paint' | 'title';
  colour?: string;
}

interface ContractDefinition {
  id: string;
  cadence: ContractCadence;
  metric: ContractMetric;
  name: string;
  description: string;
  reward: number;
  target: number;
}

interface RaceMetrics {
  finish: number;
  podium: number;
  win: number;
  style: number;
  risk: number;
  clean: number;
  proFinish: number;
}

export const CHALLENGES: readonly ChallengeDefinition[] = [
  { id: 'finish', name: 'CHECKERED FLAG', description: 'Finish any race.', reward: 120 },
  { id: 'podium', name: 'PODIUM PRESSURE', description: 'Finish in the top three against at least two rivals.', reward: 180 },
  { id: 'style', name: 'STYLE HUNTER', description: 'Earn 2,500 style in one race.', reward: 220 },
  { id: 'clean', name: 'CLEAN MACHINE', description: 'Finish with at least 85% integrity.', reward: 200 },
  { id: 'risk', name: 'RISK TAKER', description: 'Hit an outside-line risk gate.', reward: 160 },
  { id: 'combo', name: 'CHAIN REACTION', description: 'Reach a ×2.0 style multiplier.', reward: 240 },
];

export const CAREER_REWARDS: readonly CareerRewardDefinition[] = [
  { id: 'chevron', level: 2, name: 'CHEVRON WRAP', kind: 'decal' },
  { id: 'acid-paint', level: 3, name: 'ACID PULSE', kind: 'paint', colour: '#b6ff3d' },
  { id: 'circuit', level: 4, name: 'CIRCUIT WRAP', kind: 'decal' },
  { id: 'gold-paint', level: 5, name: 'GOLD RUSH', kind: 'paint', colour: '#f3c969' },
  { id: 'legend-paint', level: 6, name: 'MIDNIGHT CHROME', kind: 'paint', colour: '#718cff' },
  { id: 'aurora-paint', level: 7, name: 'AURORA FLARE', kind: 'paint', colour: '#49f2c2' },
  { id: 'city-icon', level: 8, name: 'CITY ICON', kind: 'title' },
  { id: 'redline-paint', level: 9, name: 'REDLINE PEARL', kind: 'paint', colour: '#ff394f' },
  { id: 'tour-legend', level: 10, name: 'TOUR LEGEND', kind: 'title' },
];

const DAILY_CONTRACTS: readonly ContractDefinition[] = [
  { id: 'finish', cadence: 'daily', metric: 'finish', name: 'CLOCK IN', description: 'Finish one race today.', reward: 75, target: 1 },
  { id: 'podium', cadence: 'daily', metric: 'podium', name: 'FRONT ROW', description: 'Take one podium against at least two rivals.', reward: 95, target: 1 },
  { id: 'win', cadence: 'daily', metric: 'win', name: 'DAILY TAKEOVER', description: 'Win one race with at least one rival.', reward: 125, target: 1 },
  { id: 'style', cadence: 'daily', metric: 'style', name: 'STYLE SHIFT', description: 'Bank 3,000 cumulative style today.', reward: 110, target: 3_000 },
  { id: 'risk', cadence: 'daily', metric: 'risk', name: 'OUTSIDE BET', description: 'Hit two risk lines today.', reward: 100, target: 2 },
  { id: 'clean', cadence: 'daily', metric: 'clean', name: 'CLEAN RUN', description: 'Finish once with at least 85% integrity.', reward: 100, target: 1 },
];

const WEEKLY_CONTRACTS: readonly ContractDefinition[] = [
  { id: 'finish-five', cadence: 'weekly', metric: 'finish', name: 'NIGHT REGULAR', description: 'Finish five races this week.', reward: 350, target: 5 },
  { id: 'podium-three', cadence: 'weekly', metric: 'podium', name: 'PODIUM HABIT', description: 'Take three podiums this week.', reward: 400, target: 3 },
  { id: 'win-three', cadence: 'weekly', metric: 'win', name: 'DISTRICT CONTROL', description: 'Win three races this week.', reward: 500, target: 3 },
  { id: 'style-twelve', cadence: 'weekly', metric: 'style', name: 'STYLE ECONOMY', description: 'Bank 12,000 cumulative style.', reward: 450, target: 12_000 },
  { id: 'risk-ten', cadence: 'weekly', metric: 'risk', name: 'NO SAFE LINE', description: 'Hit ten risk lines this week.', reward: 420, target: 10 },
  { id: 'clean-three', cadence: 'weekly', metric: 'clean', name: 'UNTOUCHABLE', description: 'Finish clean three times this week.', reward: 400, target: 3 },
  { id: 'pro-two', cadence: 'weekly', metric: 'pro-finish', name: 'PROVING GROUND', description: 'Finish two races against PRO rivals.', reward: 480, target: 2 },
];

const STORAGE_KEY = 'nightshift:progression:v1';
const LEVEL_THRESHOLDS = [0, 120, 360, 700, 1_120, 2_400, 3_800, 5_600, 7_800, 10_400] as const;
const CHALLENGE_IDS = new Set<ChallengeId>(CHALLENGES.map((challenge) => challenge.id));
const MEDALS = new Set<CareerMedal>(['none', 'bronze', 'silver', 'gold']);
export function createDefaultProgression(): ProgressionState {
  return {
    version: 3,
    xp: 0,
    completed: [],
    awardedRaceIds: [],
    contractProgress: {},
    contractClaims: [],
    trackMastery: {},
    rivals: {},
    recentResults: [],
    races: 0,
    wins: 0,
    bestStyle: 0,
    bestCombo: 0,
    updatedAt: Date.now(),
  };
}

export function parseProgression(raw: string | null): ProgressionState {
  const fallback = createDefaultProgression();
  if (!raw) return fallback;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (!isRecord(candidate)) return fallback;
    const completed = Array.isArray(candidate.completed)
      ? [...new Set(candidate.completed.filter((id): id is ChallengeId => CHALLENGE_IDS.has(id as ChallengeId)))]
      : [];
    return {
      version: 3,
      xp: integer(candidate.xp, 0, 1_000_000),
      completed,
      awardedRaceIds: stringArray(candidate.awardedRaceIds, validRaceId, 128),
      contractProgress: parseContractProgress(candidate.contractProgress),
      contractClaims: stringArray(candidate.contractClaims, validContractKey, 128),
      trackMastery: parseTrackMastery(candidate.trackMastery),
      rivals: parseRivals(candidate.rivals),
      recentResults: parseRecentResults(candidate.recentResults),
      races: integer(candidate.races, 0, 1_000_000),
      wins: integer(candidate.wins, 0, 1_000_000),
      bestStyle: integer(candidate.bestStyle, 0, 100_000_000),
      bestCombo: integer(candidate.bestCombo, 0, 24),
      updatedAt: finite(candidate.updatedAt, fallback.updatedAt),
    };
  } catch {
    return fallback;
  }
}

export function loadProgression(): ProgressionState {
  try {
    const career = readLocalCareerSection('progression');
    if (career !== undefined) return parseProgression(JSON.stringify(career));
    return parseProgression(localStorage.getItem(STORAGE_KEY));
  } catch {
    return createDefaultProgression();
  }
}

export function saveProgression(state: ProgressionState): void {
  const value = { ...state, version: 3 };
  const envelopeUpdated = updateLocalCareerSection('progression', value);
  if (envelopeUpdated !== null) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Career progress is optional and never blocks racing in restricted storage modes.
  }
}

export function createRaceRunStats(): RaceRunStats {
  return {
    maxCombo: 0,
    maxMultiplier: 1,
    riskLines: 0,
    routes: 0,
    perfectCorners: 0,
    actions: {},
    lastStyleEventId: -1,
  };
}

export function observeRaceRun(stats: RaceRunStats, racer: RacerSnapshot): RaceRunStats {
  const next = {
    ...stats,
    maxCombo: Math.max(stats.maxCombo, racer.styleCombo),
    maxMultiplier: Math.max(stats.maxMultiplier, racer.styleMultiplier),
    riskLines: Math.max(stats.riskLines, racer.riskLineCount),
    routes: Math.max(stats.routes, finite(racer.routeCount, 0)),
    perfectCorners: Math.max(stats.perfectCorners, finite(racer.perfectCornerCount, 0)),
  };
  if (racer.styleEventId !== stats.lastStyleEventId) {
    next.lastStyleEventId = racer.styleEventId;
    if (racer.styleAction && racer.styleAction !== 'CHAIN LOST' && racer.styleAction !== 'WHEELSPIN') {
      next.actions = {
        ...stats.actions,
        [racer.styleAction]: (stats.actions[racer.styleAction] ?? 0) + 1,
      };
    }
  }
  return next;
}

export function activeRotatingContracts(
  state: Pick<ProgressionState, 'contractProgress' | 'contractClaims'>,
  now = Date.now(),
): ActiveContract[] {
  const dailyPeriod = localDayKey(now);
  const weeklyPeriod = localWeekKey(now);
  return [
    ...selectedContracts(DAILY_CONTRACTS, dailyPeriod, 3),
    ...selectedContracts(WEEKLY_CONTRACTS, weeklyPeriod, 2),
  ].map((definition) => {
    const period = definition.cadence === 'daily' ? dailyPeriod : weeklyPeriod;
    const claimId = `${definition.cadence}:${period}:${definition.id}`;
    return {
      claimId,
      id: definition.id,
      cadence: definition.cadence,
      period,
      name: definition.name,
      description: definition.description,
      reward: definition.reward,
      target: definition.target,
      progress: Math.min(definition.target, state.contractProgress[claimId] ?? 0),
      claimed: state.contractClaims.includes(claimId),
    };
  });
}

export function completeRaceProgression(
  current: ProgressionState,
  racer: RacerSnapshot,
  totalRacers: number,
  run: RaceRunStats,
  raceId: string,
  context: Partial<CareerRaceContext> = {},
  persist = true,
): ProgressionUpdate {
  const previousLevel = progressionLevel(current.xp);
  if (validRaceId(raceId) && current.awardedRaceIds.includes(raceId)) {
    return emptyUpdate(current, previousLevel, true);
  }

  const now = finite(context.now, Date.now());
  const finished = racer.finishedAt !== null;
  const difficulty = context.npcDifficulty ?? 'rookie';
  const racers = context.racers ?? [racer];
  const normalizedContext: CareerRaceContext = {
    mapId: validIdentifier(context.mapId) || '',
    npcDifficulty: difficulty,
    timeTrial: context.timeTrial === true,
    racers,
    bestLapMs: positiveTime(context.bestLapMs),
    medal: MEDALS.has(context.medal as CareerMedal) ? context.medal as CareerMedal : 'none',
    now,
    featuredRival: typeof context.featuredRival === 'string' ? context.featuredRival.slice(0, 24) : undefined,
  };

  const eligible = new Set<ChallengeId>();
  if (finished) eligible.add('finish');
  if (finished && totalRacers >= 3 && racer.place <= 3) eligible.add('podium');
  if (racer.styleScore >= 2_500) eligible.add('style');
  if (finished && racer.health >= 85) eligible.add('clean');
  if (run.riskLines > 0) eligible.add('risk');
  if (run.maxMultiplier >= 2) eligible.add('combo');
  const alreadyCompleted = new Set(current.completed);
  const completed = CHALLENGES.filter((challenge) => eligible.has(challenge.id) && !alreadyCompleted.has(challenge.id));
  const oneTimeXp = completed.reduce((sum, challenge) => sum + challenge.reward, 0);

  const repeatBreakdown = repeatableXpBreakdown(racer, totalRacers, run, normalizedContext);
  const repeatableXp = repeatBreakdown.reduce((sum, entry) => sum + entry.xp, 0);
  const metrics = raceMetrics(racer, totalRacers, run, normalizedContext);
  const contractUpdate = updateContracts(current, metrics, now);
  const mastery = updateTrackMastery(current, racer, run, normalizedContext);
  const rivalry = updateRivals(current, racer, normalizedContext);
  const xpBreakdown: XpBreakdownEntry[] = [...repeatBreakdown];
  if (oneTimeXp > 0) xpBreakdown.push({ id: 'street-contracts', label: 'STREET CONTRACTS', xp: oneTimeXp });
  if (contractUpdate.xpEarned > 0) xpBreakdown.push({ id: 'live-contracts', label: 'LIVE CONTRACTS', xp: contractUpdate.xpEarned });
  if (mastery?.xpEarned) xpBreakdown.push({ id: 'track-mastery', label: 'TRACK MASTERY', xp: mastery.xpEarned });
  const wager = racer.wagerStatus === 'complete' ? raceWagerDefinition(racer.wagerKind) : null;
  if (wager) xpBreakdown.push({ id: `wager-${wager.id}`, label: t('WAGER // {name}', { name: t(wager.name) }), xp: wager.xp });
  const xpEarned = xpBreakdown.reduce((sum, entry) => sum + entry.xp, 0);

  const state: ProgressionState = {
    ...current,
    version: 3,
    xp: current.xp + xpEarned,
    completed: [...current.completed, ...completed.map((challenge) => challenge.id)],
    awardedRaceIds: validRaceId(raceId)
      ? [...current.awardedRaceIds, raceId].slice(-128)
      : current.awardedRaceIds,
    contractProgress: contractUpdate.progress,
    contractClaims: contractUpdate.claims,
    trackMastery: mastery
      ? { ...current.trackMastery, [mastery.mapId]: mastery.record }
      : current.trackMastery,
    rivals: rivalry.records,
    recentResults: normalizedContext.timeTrial || totalRacers < 2
      ? current.recentResults
      : [...current.recentResults, {
          place: Math.max(1, racer.place),
          total: Math.max(2, totalRacers),
          finished,
          difficulty,
          at: now,
        }].slice(-5),
    races: current.races + 1,
    wins: current.wins + Number(finished && racer.place === 1 && totalRacers >= 2),
    bestStyle: Math.max(current.bestStyle, Math.round(racer.styleScore)),
    bestCombo: Math.max(current.bestCombo, run.maxCombo),
    updatedAt: now,
  };
  if (persist) saveProgression(state);
  return {
    state,
    completed,
    completedContracts: contractUpdate.completed,
    xpBreakdown,
    xpEarned,
    repeatableXp,
    previousLevel,
    level: progressionLevel(state.xp),
    mastery,
    rivals: rivalry.deltas,
    duplicate: false,
  };
}

export function progressionLevel(xp: number): number {
  let level = 1;
  for (let index = 1; index < LEVEL_THRESHOLDS.length; index += 1) {
    if (xp < LEVEL_THRESHOLDS[index]!) break;
    level = index + 1;
  }
  return level;
}

export function progressionMeter(xp: number): { current: number; required: number; percent: number; maxed: boolean } {
  const level = progressionLevel(xp);
  const currentThreshold = LEVEL_THRESHOLDS[Math.min(level - 1, LEVEL_THRESHOLDS.length - 1)]!;
  const nextThreshold = LEVEL_THRESHOLDS[level];
  if (nextThreshold === undefined) return { current: xp - currentThreshold, required: 0, percent: 100, maxed: true };
  const required = nextThreshold - currentThreshold;
  const current = Math.max(0, xp - currentThreshold);
  return { current, required, percent: Math.min(100, (current / required) * 100), maxed: false };
}

export function rankTitle(level: number): string {
  return [
    'ROOKIE', 'STREETWISE', 'NIGHT RUNNER', 'APEX HUNTER', 'ICON',
    'NIGHT LEGEND', 'CITY HUNTER', 'CITY ICON', 'REDLINE ELITE', 'TOUR LEGEND',
  ][Math.max(0, Math.min(9, level - 1))]!;
}

export function unlockedReward(level: number, reward: CareerRewardId): boolean {
  return level >= (CAREER_REWARDS.find((candidate) => candidate.id === reward)?.level ?? Number.POSITIVE_INFINITY);
}

export function nextCareerReward(level: number): CareerRewardDefinition | null {
  return CAREER_REWARDS.find((reward) => reward.level > level) ?? null;
}

export function trackMasteryRecord(
  state: Pick<ProgressionState, 'trackMastery'>,
  mapId: string,
): TrackMasteryRecord {
  return state.trackMastery[mapId] ?? createTrackMasteryRecord();
}

export function trackMasteryProgress(record: TrackMasteryRecord, mapId = ''): {
  completed: number;
  total: number;
  percent: number;
  tier: MasteryTier;
  goals: Array<{ name: string; complete: boolean }>;
} {
  const goals = masteryGoals(mapId, record);
  const complete = goals.map((goal) => goal.complete);
  const completed = complete.filter(Boolean).length;
  return {
    completed,
    total: goals.length,
    percent: (completed / goals.length) * 100,
    tier: completed >= 6 ? 'gold' : completed >= 4 ? 'silver' : completed >= 2 ? 'bronze' : 'none',
    goals,
  };
}

export function adaptiveNpcDifficulty(
  state: Pick<ProgressionState, 'recentResults'>,
): NpcDifficulty {
  const recent = state.recentResults.slice(-3);
  if (recent.length === 0) return 'rookie';
  const performance = recent.reduce((sum, result, index) => {
    const fieldScore = result.finished
      ? 1 - (Math.max(1, result.place) - 1) / Math.max(1, result.total - 1)
      : 0;
    return sum + fieldScore * (index + 1);
  }, 0) / recent.reduce((sum, _result, index) => sum + index + 1, 0);
  if (recent.length >= 2 && performance >= 0.72) return 'pro';
  if (performance >= 0.38) return 'sport';
  return 'rookie';
}

export function currentNemesis(
  state: Pick<ProgressionState, 'rivals'>,
): { key: string; record: RivalRecord } | null {
  const ranked = Object.entries(state.rivals)
    .filter(([, rival]) => rival.lossesAgainst > rival.winsAgainst || rival.streak < 0)
    .sort(([, first], [, second]) => {
      const secondThreat = (second.lossesAgainst - second.winsAgainst) * 4 - Math.min(0, second.streak);
      const firstThreat = (first.lossesAgainst - first.winsAgainst) * 4 - Math.min(0, first.streak);
      return secondThreat - firstThreat || second.lastSeenAt - first.lastSeenAt;
    });
  const entry = ranked[0];
  return entry ? { key: entry[0], record: entry[1] } : null;
}

export function nemesisPaintUnlocked(state: Pick<ProgressionState, 'rivals'>): boolean {
  return Object.values(state.rivals).some((rival) => rival.winsAgainst >= 3);
}

function masteryGoals(
  mapId: string,
  record: TrackMasteryRecord,
): Array<{ name: string; complete: boolean }> {
  const routeNames: Record<string, string> = {
    'alpine-midnight': 'AVALANCHE GALLERY',
    'azure-coast': 'RIVIERA PANORAMA',
    'berlin-capital': 'TIERGARTEN CUT',
    'bern-altstadt': 'LAUBEN ARCADE CUT',
    'hannover-maschsee': 'HERRENHAUSEN PARK AXIS',
    'luzern-lakeside': 'MUSEGG SERVICE LANE',
    'mirror-lake-circuit': 'WATERS EDGE CUT',
    'neon-district': 'MIDNIGHT SERVICE CUT',
    'redline-canyon': 'QUARRY CUT',
    'sunspire-pass': 'DRY RIVER CUT',
  };
  const cornerNames: Record<string, string> = {
    'alpine-midnight': '3 ALPINE APEXES',
    'azure-coast': '3 COAST APEXES',
    'berlin-capital': '2 CAPITAL APEXES',
    'bern-altstadt': '2 OLD-TOWN APEXES',
    'hannover-maschsee': '2 LAKESIDE APEXES',
    'luzern-lakeside': '2 QUAI APEXES',
    'mirror-lake-circuit': '3 MOUNTAIN APEXES',
    'neon-district': '3 NEON APEXES',
    'redline-canyon': '3 CANYON APEXES',
    'sunspire-pass': '3 SUMMIT APEXES',
  };
  const cornerTarget = ['alpine-midnight', 'azure-coast', 'mirror-lake-circuit', 'neon-district', 'redline-canyon', 'sunspire-pass'].includes(mapId)
    ? 3
    : 2;
  if (!routeNames[mapId]) {
    return [
      { name: 'FIRST FINISH', complete: record.finishes >= 1 },
      { name: 'FIVE FINISHES', complete: record.finishes >= 5 },
      { name: 'FIRST WIN', complete: record.wins >= 1 },
      { name: 'THREE PODIUMS', complete: record.podiums >= 3 },
      { name: '2,500 STYLE', complete: record.bestStyle >= 2_500 },
      { name: 'CLEAN FINISH', complete: record.bestIntegrity >= 85 },
    ];
  }
  return [
    { name: 'FIRST FINISH', complete: record.finishes >= 1 },
    { name: routeNames[mapId]!, complete: record.bestRoutes >= 1 },
    { name: cornerNames[mapId]!, complete: record.bestPerfectCorners >= cornerTarget },
    { name: 'CLEAN FINISH', complete: record.bestIntegrity >= 85 },
    { name: 'FIRST WIN', complete: record.wins >= 1 },
    { name: '2,500 STYLE', complete: record.bestStyle >= 2_500 },
  ];
}

function repeatableXpBreakdown(
  racer: RacerSnapshot,
  totalRacers: number,
  run: RaceRunStats,
  context: CareerRaceContext,
): XpBreakdownEntry[] {
  const finished = racer.finishedAt !== null;
  const entries: XpBreakdownEntry[] = [];
  if (finished) entries.push({ id: 'finish', label: 'RACE FINISH', xp: 45 });
  const positionXp = !finished
    ? 0
    : context.timeTrial || totalRacers <= 1
      ? 45
      : racer.place === 1 ? 90 : racer.place <= 3 ? 60 : 35;
  if (positionXp > 0) entries.push({ id: 'position', label: context.timeTrial ? 'TIMED EVENT' : 'FINISHING POSITION', xp: positionXp });
  const hasNpcRivals = !context.timeTrial
    && context.racers.some((candidate) => candidate.npc && candidate.id !== racer.id);
  const difficultyMultiplier = !hasNpcRivals
    ? 1
    : context.npcDifficulty === 'pro' ? 1.55 : context.npcDifficulty === 'sport' ? 1.25 : 1;
  const difficultyXp = finished ? Math.round((45 + positionXp) * (difficultyMultiplier - 1)) : 0;
  if (difficultyXp > 0) entries.push({ id: 'difficulty', label: t('{difficulty} RIVALS', { difficulty: t(context.npcDifficulty.toUpperCase()) }), xp: difficultyXp });
  const styleXp = Math.min(80, Math.floor(Math.max(0, racer.styleScore) / 500) * 8);
  if (styleXp > 0) entries.push({ id: 'style', label: 'STYLE BANK', xp: styleXp });
  const cleanXp = finished && racer.health >= 85 ? 40 : finished && racer.health >= 60 ? 15 : 0;
  if (cleanXp > 0) entries.push({ id: 'integrity', label: racer.health >= 85 ? 'CLEAN FINISH' : 'SURVIVAL', xp: cleanXp });
  const riskXp = Math.min(45, run.riskLines * 15);
  if (riskXp > 0) entries.push({ id: 'risk', label: 'RISK LINES', xp: riskXp });
  const comboXp = Math.min(30, Math.max(0, Math.floor((run.maxMultiplier - 1) * 20)));
  if (comboXp > 0) entries.push({ id: 'combo', label: 'STYLE CHAIN', xp: comboXp });
  const featuredRival = context.featuredRival
    ? context.racers.find((candidate) => candidate.npc && candidate.name.toLowerCase() === context.featuredRival?.toLowerCase())
    : null;
  if (finished && featuredRival && racer.place < featuredRival.place) {
    entries.push({ id: 'rival-bounty', label: t('RIVAL BOUNTY // {name}', { name: featuredRival.name.toUpperCase() }), xp: 125 });
  }
  return entries;
}

function raceMetrics(
  racer: RacerSnapshot,
  totalRacers: number,
  run: RaceRunStats,
  context: CareerRaceContext,
): RaceMetrics {
  const finished = racer.finishedAt !== null;
  const hasProRivals = !context.timeTrial
    && context.npcDifficulty === 'pro'
    && context.racers.some((candidate) => candidate.npc && candidate.id !== racer.id);
  return {
    finish: Number(finished),
    podium: Number(finished && totalRacers >= 3 && racer.place <= 3),
    win: Number(finished && totalRacers >= 2 && racer.place === 1),
    style: Math.max(0, Math.round(racer.styleScore)),
    risk: Math.max(0, run.riskLines),
    clean: Number(finished && racer.health >= 85),
    proFinish: Number(finished && hasProRivals),
  };
}

function updateContracts(
  current: ProgressionState,
  metrics: RaceMetrics,
  now: number,
): { progress: Record<string, number>; claims: string[]; completed: ActiveContract[]; xpEarned: number } {
  const progress = { ...current.contractProgress };
  const claims = [...current.contractClaims];
  const completed: ActiveContract[] = [];
  const active = activeRotatingContracts(current, now);
  for (const contract of active) {
    const definition = [...DAILY_CONTRACTS, ...WEEKLY_CONTRACTS].find((candidate) => candidate.id === contract.id && candidate.cadence === contract.cadence);
    if (!definition) continue;
    const metricValue = contractMetricValue(metrics, definition.metric);
    const nextProgress = Math.min(contract.target, (progress[contract.claimId] ?? 0) + metricValue);
    progress[contract.claimId] = nextProgress;
    if (nextProgress < contract.target || claims.includes(contract.claimId)) continue;
    claims.push(contract.claimId);
    completed.push({ ...contract, progress: nextProgress, claimed: true });
  }
  const activeKeys = new Set(active.map((contract) => contract.claimId));
  const recentProgress = Object.fromEntries(
    Object.entries(progress)
      .filter(([key]) => activeKeys.has(key) || current.contractClaims.includes(key))
      .slice(-128),
  );
  return {
    progress: recentProgress,
    claims: claims.slice(-128),
    completed,
    xpEarned: completed.reduce((sum, contract) => sum + contract.reward, 0),
  };
}

function updateTrackMastery(
  current: ProgressionState,
  racer: RacerSnapshot,
  run: RaceRunStats,
  context: CareerRaceContext,
): TrackMasteryUpdate | null {
  if (!context.mapId) return null;
  const previousRecord = trackMasteryRecord(current, context.mapId);
  const nextRecord = buildNextMasteryRecord(previousRecord, racer, run, context);
  const before = trackMasteryProgress(previousRecord, context.mapId);
  const after = trackMasteryProgress(nextRecord, context.mapId);
  const newGoals = after.goals.filter((goal, index) => goal.complete && !before.goals[index]?.complete).map((goal) => goal.name);
  const tierBonus = tierRank(after.tier) > tierRank(before.tier)
    ? after.tier === 'gold' ? 120 : after.tier === 'silver' ? 70 : 40
    : 0;
  return {
    mapId: context.mapId,
    before,
    after,
    newGoals,
    xpEarned: newGoals.length * 35 + tierBonus,
    record: nextRecord,
  };
}

function buildNextMasteryRecord(
  previous: TrackMasteryRecord,
  racer: RacerSnapshot,
  run: RaceRunStats,
  context: CareerRaceContext,
): TrackMasteryRecord {
  const finished = racer.finishedAt !== null;
  return {
    races: previous.races + 1,
    finishes: previous.finishes + Number(finished),
    wins: previous.wins + Number(finished && racer.place === 1 && context.racers.length >= 2),
    podiums: previous.podiums + Number(finished && racer.place <= 3 && context.racers.length >= 3),
    bestPlace: finished ? Math.min(previous.bestPlace, Math.max(1, racer.place)) : previous.bestPlace,
    bestStyle: Math.max(previous.bestStyle, Math.round(racer.styleScore)),
    bestIntegrity: finished ? Math.max(previous.bestIntegrity, Math.round(racer.health)) : previous.bestIntegrity,
    bestLapMs: minPositive(previous.bestLapMs, context.bestLapMs),
    bestMedal: betterMedal(previous.bestMedal, context.medal),
    bestRoutes: Math.max(previous.bestRoutes, finite(run.routes, 0)),
    bestPerfectCorners: Math.max(previous.bestPerfectCorners, finite(run.perfectCorners, 0)),
    updatedAt: context.now,
  };
}

function updateRivals(
  current: ProgressionState,
  local: RacerSnapshot,
  context: CareerRaceContext,
): { records: Record<string, RivalRecord>; deltas: RivalDelta[] } {
  const records = { ...current.rivals };
  const deltas: RivalDelta[] = [];
  for (const rival of context.racers.filter((candidate) => candidate.npc && candidate.id !== local.id)) {
    const key = rivalKey(rival.name, rival.id);
    const previous = records[key] ?? createRivalRecord(rival.name);
    // A DNF/busted Heat can still rank highly on provisional score. It must
    // never become a career win over a rival unless the local racer finished.
    const won = local.finishedAt !== null && local.place < rival.place;
    const record: RivalRecord = {
      name: rival.name.slice(0, 24),
      races: previous.races + 1,
      winsAgainst: previous.winsAgainst + Number(won),
      lossesAgainst: previous.lossesAgainst + Number(!won),
      bestFinish: Math.min(previous.bestFinish, Math.max(1, rival.place)),
      streak: won ? Math.max(1, previous.streak + 1) : Math.min(-1, previous.streak - 1),
      carIds: [...new Set([...previous.carIds, rival.carId])].slice(-4),
      lastSeenAt: context.now,
    };
    records[key] = record;
    deltas.push({ key, name: record.name, result: won ? 'won' : 'lost', record });
  }
  const trimmed = Object.fromEntries(
    Object.entries(records)
      .sort(([, first], [, second]) => first.lastSeenAt - second.lastSeenAt)
      .slice(-24),
  );
  return { records: trimmed, deltas };
}

function emptyUpdate(state: ProgressionState, level: number, duplicate: boolean): ProgressionUpdate {
  return {
    state,
    completed: [],
    completedContracts: [],
    xpBreakdown: [],
    xpEarned: 0,
    repeatableXp: 0,
    previousLevel: level,
    level,
    mastery: null,
    rivals: [],
    duplicate,
  };
}

function selectedContracts(
  definitions: readonly ContractDefinition[],
  period: string,
  count: number,
): ContractDefinition[] {
  return [...definitions]
    .sort((first, second) => stableHash(`${period}:${first.id}`) - stableHash(`${period}:${second.id}`))
    .slice(0, count);
}

function contractMetricValue(metrics: RaceMetrics, metric: ContractMetric): number {
  if (metric === 'pro-finish') return metrics.proFinish;
  return metrics[metric];
}

function localDayKey(now: number): string {
  const date = new Date(now);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function localWeekKey(now: number): string {
  const source = new Date(now);
  const monday = new Date(source.getFullYear(), source.getMonth(), source.getDate(), 12);
  const daysFromMonday = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - daysFromMonday);
  return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
}

function createTrackMasteryRecord(): TrackMasteryRecord {
  return {
    races: 0,
    finishes: 0,
    wins: 0,
    podiums: 0,
    bestPlace: 99,
    bestStyle: 0,
    bestIntegrity: 0,
    bestLapMs: null,
    bestMedal: 'none',
    bestRoutes: 0,
    bestPerfectCorners: 0,
    updatedAt: 0,
  };
}

function createRivalRecord(name: string): RivalRecord {
  return {
    name: name.slice(0, 24),
    races: 0,
    winsAgainst: 0,
    lossesAgainst: 0,
    bestFinish: 99,
    streak: 0,
    carIds: [],
    lastSeenAt: 0,
  };
}

function parseContractProgress(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => validContractKey(key))
      .slice(-128)
      .map(([key, progress]) => [key, integer(progress, 0, 1_000_000)]),
  );
}

function parseTrackMastery(value: unknown): Record<string, TrackMasteryRecord> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([mapId, record]) => Boolean(validIdentifier(mapId)) && isRecord(record))
      .slice(-64)
      .map(([mapId, value]) => {
        const record = value as Record<string, unknown>;
        return [mapId, {
          races: integer(record.races, 0, 1_000_000),
          finishes: integer(record.finishes, 0, 1_000_000),
          wins: integer(record.wins, 0, 1_000_000),
          podiums: integer(record.podiums, 0, 1_000_000),
          bestPlace: integerWithFallback(record.bestPlace, 1, 99, 99),
          bestStyle: integer(record.bestStyle, 0, 100_000_000),
          bestIntegrity: integer(record.bestIntegrity, 0, 100),
          bestLapMs: positiveTime(record.bestLapMs),
          bestMedal: MEDALS.has(record.bestMedal as CareerMedal) ? record.bestMedal as CareerMedal : 'none',
          bestRoutes: integer(record.bestRoutes, 0, 1_000),
          bestPerfectCorners: integer(record.bestPerfectCorners, 0, 1_000),
          updatedAt: finite(record.updatedAt, 0),
        } satisfies TrackMasteryRecord];
      }),
  );
}

function parseRivals(source: unknown): Record<string, RivalRecord> {
  if (!isRecord(source)) return {};
  const parsed: Array<[string, RivalRecord]> = [];
  for (const [key, value] of Object.entries(source)) {
    if (!validIdentifier(key) || !isRecord(value)) continue;
    parsed.push([key, {
      name: typeof value.name === 'string' ? value.name.trim().slice(0, 24) : 'RIVAL',
      races: integer(value.races, 0, 1_000_000),
      winsAgainst: integer(value.winsAgainst, 0, 1_000_000),
      lossesAgainst: integer(value.lossesAgainst, 0, 1_000_000),
      bestFinish: integerWithFallback(value.bestFinish, 1, 99, 99),
      streak: integer(value.streak, -1_000, 1_000),
      carIds: stringArray(value.carIds, (entry) => Boolean(validIdentifier(entry)), 4),
      lastSeenAt: finite(value.lastSeenAt, 0),
    }]);
  }
  parsed.sort(([, first], [, second]) => first.lastSeenAt - second.lastSeenAt);
  return Object.fromEntries(parsed.slice(-24));
}

function parseRecentResults(source: unknown): RecentRaceResult[] {
  if (!Array.isArray(source)) return [];
  return source
    .filter(isRecord)
    .map((value): RecentRaceResult | null => {
      const difficulty = value.difficulty;
      if (!['rookie', 'sport', 'pro'].includes(String(difficulty))) return null;
      const total = integer(value.total, 2, 12);
      return {
        place: integer(value.place, 1, total),
        total,
        finished: value.finished === true,
        difficulty: difficulty as NpcDifficulty,
        at: finite(value.at, 0),
      };
    })
    .filter((value): value is RecentRaceResult => value !== null)
    .slice(-5);
}

function stringArray(
  value: unknown,
  predicate: (value: unknown) => boolean,
  maximum: number,
): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((entry): entry is string => typeof entry === 'string' && predicate(entry)))].slice(-maximum);
}

function betterMedal(first: CareerMedal, second: CareerMedal): CareerMedal {
  return tierRank(second) > tierRank(first) ? second : first;
}

function tierRank(tier: MasteryTier | CareerMedal): number {
  return tier === 'gold' ? 3 : tier === 'silver' ? 2 : tier === 'bronze' ? 1 : 0;
}

function minPositive(first: number | null, second: number | null): number | null {
  if (first === null) return second;
  if (second === null) return first;
  return Math.min(first, second);
}

function positiveTime(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= 60 * 60 * 1_000 ? Math.round(parsed) : null;
}

function rivalKey(name: string, fallback: string): string {
  const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);
  return key || validIdentifier(fallback) || 'rival';
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function integer(value: unknown, minimum: number, maximum: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, Math.min(maximum, Math.round(parsed))) : minimum;
}

function integerWithFallback(value: unknown, minimum: number, maximum: number, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, Math.min(maximum, Math.round(parsed))) : fallback;
}

function finite(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function validIdentifier(value: unknown): string {
  return typeof value === 'string' && value.length >= 1 && value.length <= 96 && /^[a-zA-Z0-9_-]+$/.test(value) ? value : '';
}

function validRaceId(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 3 && value.length <= 96 && /^[a-zA-Z0-9_-]+$/.test(value);
}

function validContractKey(value: unknown): boolean {
  return typeof value === 'string' && value.length >= 6 && value.length <= 128 && /^[a-z0-9:-]+$/i.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
